import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from './service';
import { useActiveSubscription } from './useSubscriptionQueries';

const CurrentPlan = () => {
  const token = useSelector((state) => state.auth.token);
  const navigate = useNavigate();

  // Use React Query hook for active subscription
  const { 
    data: subscription = null, 
    isLoading: loading, 
    error: subscriptionError 
  } = useActiveSubscription(token);

  const formatBillingCycle = (cycle) => {
    if (!cycle) return '';
    const value = cycle.toLowerCase();
    switch (value) {
      case 'daily':
        return '/day';
      case 'weekly':
        return '/week';
      case 'monthly':
        return '/month';
      case 'yearly':
        return '/year';
      default:
        return `/${value}`;
    }
  };

  const formatExpirationDate = (date) => {
    if (!date) return 'N/A';
    try {
      const expirationDate = new Date(date);
      return expirationDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return 'N/A';
    }
  };

  const handleUpgradePlan = () => {
    // Navigate to subscription pricing page
    navigate('/user/profile/settings');
    // Scroll to pricing section if needed
    setTimeout(() => {
      const pricingSection = document.querySelector('.pricing-table-outer');
      if (pricingSection) {
        pricingSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  if (loading) {
    return (
      <div className="current-plan">
        <div className='current-plan-inner'>
          <div className='current-plan-inner-top' style={{ marginBottom: '7px' }}>
            {/* Plan Title Skeleton */}
            <div
              style={{
                width: "280px",
                height: "20px",
                backgroundColor: "#e5e7eb",
                borderRadius: "4px",
                animation: "pulse 1.5s ease-in-out infinite",
                marginBottom: "5px",
              }}
            />
            {/* Plan Description Skeleton */}
            <div
              style={{
                width: "400px",
                height: "14px",
                backgroundColor: "#e5e7eb",
                borderRadius: "4px",
                animation: "pulse 1.5s ease-in-out infinite",
                marginBottom: "8px",
              }}
            />
            {/* Expiration Date Skeleton */}
            <div
              style={{
                width: "200px",
                height: "14px",
                backgroundColor: "#e5e7eb",
                borderRadius: "4px",
                animation: "pulse 1.5s ease-in-out infinite",
              }}
            />
          </div>
          <div className='current-plan-inner-bottom'>
            {/* Price Skeleton */}
            <div
              style={{
                width: "120px",
                height: "20px",
                backgroundColor: "#e5e7eb",
                borderRadius: "4px",
                animation: "pulse 1.5s ease-in-out infinite",
              }}
            />
            {/* Button Skeleton */}
            <div className='upgrade-plan-btn'>
              <div
                style={{
                  width: "130px",
                  height: "40px",
                  backgroundColor: "#e5e7eb",
                  borderRadius: "8px",
                  animation: "pulse 1.5s ease-in-out infinite",
                }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (subscriptionError && !subscription) {
    return (
      <div className="current-plan">
        <div className='current-plan-inner'>
          <div style={{ textAlign: 'center', padding: '2rem', color: '#ef4444' }}>
            {subscriptionError?.response?.data?.message || subscriptionError?.message || 'Failed to load subscription'}
          </div>
        </div>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="current-plan">
        <div className='current-plan-inner'>
          <div className='current-plan-inner-top'>
            <h3>No Active Plan</h3>
            <p>You don't have an active subscription plan. Choose a plan to get started.</p>
          </div>
          <div className='current-plan-inner-bottom'>
            <div className='upgrade-plan-btn'>
              <button onClick={handleUpgradePlan}>Choose Plan</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const billingCycleLabel = formatBillingCycle(subscription.billingCycle);
  const price = formatCurrency(subscription.amount, subscription.currency);
  const expirationDate = formatExpirationDate(subscription.expirationDate);
  const isExpired = subscription.isExpired;

  return (
    <div className="current-plan">
      <div className='current-plan-inner'>
        <div className='current-plan-inner-top'>
          <h3>
            Your current plan: {subscription.planName}
            {isExpired && (
              <span style={{ 
                color: '#ef4444', 
                fontSize: '14px', 
                fontWeight: 'normal',
                marginLeft: '10px'
              }}>
                (Expired)
              </span>
            )}
          </h3>
          <p>{subscription.planDescription}</p>
          {expirationDate && expirationDate !== 'N/A' && (
            <p style={{ 
              fontSize: '14px', 
              color: isExpired ? '#ef4444' : '#666',
              marginTop: '8px'
            }}>
              {isExpired ? 'Expired on' : 'Expires on'}: {expirationDate}
            </p>
          )}
        </div>
        <div className='current-plan-inner-bottom'>
          <div className='current-plan-price'>
            {price}{billingCycleLabel}
          </div>
          <div className='upgrade-plan-btn'>
            <button onClick={handleUpgradePlan}>
              {isExpired ? 'Renew Plan' : 'Upgrade Plan'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CurrentPlan;